import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import React, { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { useTheme } from "@mui/material/styles";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import D365ExistsIcon from "./D365ExistsIcon";
import { getLineStatusChipStyle } from "./lineStatusColors";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import { getNodeCardSx } from "./nodeCardStyles";
import NodeToolbar from "./NodeToolbar";

const globalEntityTypes = new Set(["Product"]);

function EntityNode({ data }: NodeProps<ExplorerNode>) {
    const theme = useTheme();
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);
    const isDark = theme.palette.mode === "dark";

    return (
        <>
            <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
            <Card
                elevation={0}
                sx={getNodeCardSx({ nodeColor, isSelected: data.isSelected, isDark, width: 260, source: data.source })}
            >
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <NodeToolbar
                        nodeColor={nodeColor}
                        title={data.nodeTitle}
                        subtitle={data.nodeSubtitle}
                        icon={data.make !== undefined
                            ? (data.make ? PrecisionManufacturingIcon : ShoppingCartIcon)
                            : getNodeIcon(data.entityType)
                        }
                        titleAdornment={
                            isLoading
                                ? <CircularProgress size={12} />
                                : data.d365Exists !== undefined
                                    ? <D365ExistsIcon exists={data.d365Exists ?? null} />
                                    : undefined
                        }
                        dataAreaId={data.countryCode ? null : data.dataAreaId}
                        countryCode={data.countryCode}
                        global={globalEntityTypes.has(data.entityType)}
                        onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
                    />

                    {(data.orderStatus || data.summaryFields.length > 0) && (
                        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            {data.orderStatus && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            opacity: 0.8
                                        }}>Status</Typography>
                                    <Chip
                                        label={data.orderStatus}
                                        size="small"
                                        sx={{
                                            height: 16,
                                            fontSize: "0.65rem",
                                            fontWeight: 600,
                                            ...getLineStatusChipStyle(data.orderStatus, isDark),
                                        }}
                                    />
                                </Stack>
                            )}

                            {data.summaryFields.map((field, i) => (
                                <Stack key={i} direction="row" spacing={1} sx={{ justifyContent: "space-between" }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.secondary",
                                            opacity: 0.8,
                                        }}
                                    >
                                        {field.label}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                        {field.value ?? "\u2014"}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>
            <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
            <Handle type="source" position={Position.Bottom} id="bottom" style={{ opacity: 0 }} />
        </>
    );
}

export default memo(EntityNode);
